import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import { ACCENT, formatMXN } from "../cuentas/shared";
import { formatCalendarDate } from "../../utils/dates";
import { variacion, resumenEventos } from "../../utils/reportes";
import { Kpi } from "./shared";

// Rentabilidad evento por evento: la tabla completa del período en orden
// cronológico (lo más reciente primero). La pregunta que contesta: "¿cuáles
// eventos me dejan y cuáles me cuestan?" — los negativos se resaltan solos.
const ReporteRentabilidad = ({ rango }) => {
  const navigate = useNavigate();
  const { events, costos, calendarTimezone: tz } = useVenue();

  const evts = useMemo(
    () => resumenEventos({ events, costos, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [events, costos, rango, tz]
  );
  // resumenEventos ordena por utilidad; aquí la lectura natural es la línea
  // de tiempo.
  const filas = useMemo(() => {
    const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
    return [...evts.porEvento].sort((a, b) => t(b.startTime) - t(a.startTime));
  }, [evts]);

  // Fila expandida: de qué se compone el valor (la cotización) y qué costos
  // se lo comieron — el mismo patrón del Estado de resultados.
  const [eventoAbierto, setEventoAbierto] = useState(null);
  const detalleAbierto = useMemo(() => {
    if (!eventoAbierto) return null;
    const ev = (events || []).find((e) => e.id === eventoAbierto) || null;
    const lineas = Array.isArray(ev?.desglose) ? ev.desglose : [];
    const costosEv = (costos || [])
      .filter((c) => c.eventId === eventoAbierto)
      .sort((a, b) => {
        const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
        return t(a.fecha) - t(b.fecha);
      });
    return { ev, lineas, costosEv };
  }, [eventoAbierto, events, costos]);
  const evtsPrev = useMemo(
    () => resumenEventos({ events, costos, desde: rango.prev.from, hasta: rango.prev.to, timeZone: tz }),
    [events, costos, rango, tz]
  );

  const enNegativo = evts.porEvento.filter((e) => e.utilidad < 0).length;
  const sinCostos = evts.porEvento.filter((e) => e.costos === 0).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Eventos"
          value={evts.eventos}
          delta={variacion(evts.eventos, evtsPrev.eventos)}
          hint={evts.cancelados > 0 ? `${evts.cancelados} cancelados aparte` : null}
        />
        <Kpi
          label="Utilidad total"
          value={formatMXN(evts.utilidad)}
          delta={variacion(evts.utilidad, evtsPrev.utilidad)}
          tone={evts.utilidad < 0 ? "text-red-600" : "text-emerald-600"}
          hint={evts.margen !== null ? `Margen ${(evts.margen * 100).toFixed(1)}%` : null}
        />
        <Kpi
          label="Valor promedio"
          value={formatMXN(evts.valorPromedio)}
          delta={variacion(evts.valorPromedio, evtsPrev.valorPromedio)}
          hint="Por evento del período"
        />
        <Kpi
          label="En negativo"
          value={enNegativo}
          tone={enNegativo > 0 ? "text-red-600" : ""}
          hint={enNegativo > 0 ? "Eventos donde los costos superaron el valor" : "Ningún evento perdió dinero"}
        />
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        {evts.eventos === 0 ? (
          <p className="text-sm text-zinc-400 p-8 text-center">
            No hay eventos en este período.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Evento</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Valor</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Costos</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Utilidad</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Margen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {filas.map((e) => {
                  const margen = e.valor > 0 ? e.utilidad / e.valor : null;
                  const abierto = eventoAbierto === e.id;
                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        onClick={() => setEventoAbierto(abierto ? null : e.id)}
                        className={`cursor-pointer transition hover:bg-zinc-50 dark:hover:bg-zinc-800/40 ${
                          e.utilidad < 0 ? "bg-red-50/40 dark:bg-red-500/5" : ""
                        }`}
                        title="Ver de qué se compone"
                      >
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1">
                            <ChevronRightIcon
                              className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${abierto ? "rotate-90" : ""}`}
                            />
                            <button
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/dashboard/eventos/${e.id}`);
                              }}
                              className="hover:underline text-left font-medium"
                              style={{ color: ACCENT }}
                            >
                              {e.nombre}
                            </button>
                          </span>
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-zinc-500">
                          {formatCalendarDate(e.startTime, tz, { style: "short", weekday: "short", instante: true }) || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right text-zinc-600 whitespace-nowrap">
                          {e.valor > 0 ? formatMXN(e.valor) : <Badge color="zinc">Sin valor</Badge>}
                        </td>
                        <td className="px-4 py-2.5 text-right text-red-600 whitespace-nowrap">
                          {e.costos > 0 ? formatMXN(e.costos) : "—"}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${
                            e.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                          }`}
                        >
                          {formatMXN(e.utilidad)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-zinc-500 whitespace-nowrap">
                          {margen !== null ? `${(margen * 100).toFixed(0)}%` : "—"}
                        </td>
                      </tr>
                      {abierto && detalleAbierto && (
                        <tr>
                          <td colSpan={6} className="px-5 py-4 bg-zinc-50/70 dark:bg-zinc-800/30">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left">
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                  Cotización · {formatMXN(e.valor)}
                                </div>
                                {detalleAbierto.lineas.length === 0 ? (
                                  <p className="text-xs text-zinc-400">
                                    Sin desglose: el valor es el precio acordado.
                                  </p>
                                ) : (
                                  <div className="space-y-1">
                                    {detalleAbierto.lineas.map((l, idx) => {
                                      const totalLinea =
                                        (Number(l.units) || 0) * (Number(l.unitPrice) || 0);
                                      return (
                                        <div key={idx} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-700 dark:text-zinc-300 truncate">
                                            {l.concept || "—"}
                                            {Number(l.units) > 1 && (
                                              <span className="text-zinc-400">
                                                {" "}· {l.units} × {formatMXN(l.unitPrice)}
                                              </span>
                                            )}
                                          </span>
                                          <span
                                            className={`ml-auto font-medium whitespace-nowrap ${
                                              totalLinea < 0
                                                ? "text-red-600"
                                                : "text-zinc-900 dark:text-white"
                                            }`}
                                          >
                                            {formatMXN(totalLinea)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {detalleAbierto.ev?.ivaEnabled && (
                                      <div className="flex items-baseline gap-2 text-xs pt-1 border-t border-zinc-200/60 dark:border-zinc-700/40">
                                        <span className="text-zinc-400">IVA (16%)</span>
                                        <span className="ml-auto text-zinc-500 whitespace-nowrap">
                                          {formatMXN(
                                            detalleAbierto.lineas.reduce(
                                              (s, l) =>
                                                s + (Number(l.units) || 0) * (Number(l.unitPrice) || 0),
                                              0
                                            ) * 0.16
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                  Costos del evento · {e.costos > 0 ? formatMXN(e.costos) : "—"}
                                </div>
                                {detalleAbierto.costosEv.length === 0 ? (
                                  <p className="text-xs text-zinc-400">
                                    Sin costos capturados: la utilidad es el valor completo.
                                  </p>
                                ) : (
                                  <div className="space-y-1">
                                    {detalleAbierto.costosEv.map((c) => (
                                      <div key={c.id} className="flex items-baseline gap-2 text-xs">
                                        <span className="text-zinc-400 w-16 shrink-0">
                                          {formatCalendarDate(c.fecha, tz, { style: "short" })}
                                        </span>
                                        <span className="text-zinc-700 dark:text-zinc-300 truncate">
                                          {c.concepto || "—"}
                                          {c.categoria && (
                                            <span className="text-zinc-400"> · {c.categoria}</span>
                                          )}
                                        </span>
                                        {c.estado === "por_pagar" && (
                                          <span className="text-amber-600 whitespace-nowrap">por pagar</span>
                                        )}
                                        <span className="ml-auto font-medium text-red-600 whitespace-nowrap">
                                          {formatMXN(c.monto)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700/60">
                <tr>
                  <td className="px-4 py-2.5 font-semibold text-zinc-900 dark:text-white" colSpan={2}>
                    Total
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white whitespace-nowrap">
                    {formatMXN(evts.valor)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">
                    {formatMXN(evts.costos)}
                  </td>
                  <td
                    className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${
                      evts.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {formatMXN(evts.utilidad)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-zinc-500 whitespace-nowrap">
                    {evts.margen !== null ? `${(evts.margen * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {sinCostos > 0 && evts.eventos > 0 && (
        <p className="text-xs text-zinc-400">
          {sinCostos} {sinCostos === 1 ? "evento no tiene" : "eventos no tienen"} costos
          capturados: su utilidad es su valor completo. Los costos se registran
          en el tab Balance de cada evento.
        </p>
      )}
    </div>
  );
};

export default ReporteRentabilidad;
